// File: ClusterLegend.tsx

import React, { FC, useState, memo, useCallback } from "react"; // Added useCallback
import { ScrollArea } from "@/components/ui/scroll-area";
import { ClusterLegendProps, ClusterStats } from "@/types/BigDataMap.types"; // Ensure types are imported, remove ViewStateType/ExportMapData if not used
import { Button } from "../ui/button";
import { FilterX, Filter } from "lucide-react";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Card, CardContent } from "@/components/ui/card"; // Removed unused CardHeader, CardTitle, CardDescription

/**
 * ClusterLegend Component - Updated
 *
 * Notifies the parent component to handle zooming via onClusterZoomRequest.
 * Uses the parent's resetView function for clearing filters and resetting state/view.
 *
 * Props:
 * - clusterColors: Object mapping cluster numbers to their respective colors.
 * - filteredCluster: Currently filtered cluster number, if any.
 * - setFilteredCluster: Function to set the filtered cluster.
 * - data: Array of data points (potentially removable if stats calculation moves to parent).
 * - resetView: Function to reset the map state/view to its initial calculated state.
 * - calculateClusterStats: Function to calculate statistics for a cluster.
 * - setSelectedCluster: Function to set the selected cluster.
 * - setSelectedClusterStats: Function to set the selected cluster's statistics.
 * - setSelectionMode: Function to set the selection mode ("region" or "cluster").
 * - onClusterZoomRequest: Callback function requesting the parent to zoom to a specific cluster.
 */
const ClusterLegend: FC<ClusterLegendProps> = ({
  clusterColors,
  filteredCluster,
  setFilteredCluster,
  data, // Still needed for calculateClusterStats as currently implemented
  // setViewState, // Removed
  resetView, // Use this for all resets
  calculateClusterStats,
  setSelectedCluster,
  setSelectedClusterStats,
  setSelectionMode,
  onClusterZoomRequest, // Added callback for zoom requests
}) => {
  const [isCardOpen, setIsCardOpen] = useState(false); // State to manage Card visibility on large screens

  // --- Handler for clicking a cluster in the list ---
  // Use useCallback to potentially optimize child re-renders if needed, though dependencies are many
  const handleClusterClick = useCallback((clusterNum: number) => {
    if (filteredCluster === clusterNum) {
      // Clicked the currently active filter - reset everything via parent
      resetView();
    } else {
      // Clicked a new filter or no filter was active
      setFilteredCluster(clusterNum);
      setSelectedCluster(clusterNum); // Selecting implies filtering here

      // Calculate stats locally
      const stats = calculateClusterStats(clusterNum);
      setSelectedClusterStats(stats); // Set stats even if null/empty

      setSelectionMode("cluster"); // Ensure mode is cluster

      // --- Request zoom from parent ---
      onClusterZoomRequest(clusterNum);
      // --- End Request zoom ---
    }
    // Close the card/drawer after interaction
    setIsCardOpen(false); // Close card on desktop
    // Note: Drawer needs manual closing if interaction should close it immediately
  }, [
      filteredCluster,
      resetView,
      setFilteredCluster,
      setSelectedCluster,
      calculateClusterStats,
      setSelectedClusterStats,
      setSelectionMode,
      onClusterZoomRequest,
      // Removed data dependency as it's stable or handled by parent re-render
  ]);


   // --- Handler for the dedicated "Clear Filter" buttons ---
   const handleClearFilterClick = useCallback(() => {
    resetView(); // Parent handles resetting the view and associated state
    toast("Cluster filter cleared");
    setIsCardOpen(false); // Ensure card closes if open
  }, [resetView]);


  // --- Reusable Content Component ---
  // No changes needed in the content rendering itself from previous good version
  const ClusterLegendContent = ({ closeContainer }: { closeContainer?: () => void }) => (
     <>
       <ul className="text-xs p-2"> {/* Added padding */}
         {Object.keys(clusterColors)
           .map(Number) // Ensure keys are numbers for sorting if desired
           .sort((a, b) => a - b) // Sort clusters numerically
           .map((clusterNum) => (
             <li
               key={clusterNum}
               className={`flex items-center p-2 cursor-pointer hover:bg-accent/50 rounded ${ // Added padding
                 filteredCluster === clusterNum ? "bg-accent font-semibold" : "" // Added font-semibold
               }`}
               onClick={() => {
                 handleClusterClick(clusterNum);
                 closeContainer?.(); // Call close callback if provided (for Drawer)
               }}
               role="button" // Add role for accessibility
               tabIndex={0} // Make it focusable
               onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClusterClick(clusterNum); }} // Keyboard interaction
             >
               <span
                 className="inline-block w-3 h-3 mr-2 border border-border flex-shrink-0 rounded-sm" // Added rounded-sm
                 style={{
                   backgroundColor: `rgba(${clusterColors[clusterNum].join(', ')})`, // Simplified rgba string
                 }}
                 aria-label={`Color for Cluster ${clusterNum}`} // Accessibility
               />
               Cluster {clusterNum}
             </li>
           ))}
       </ul>
     </>
   );

  // --- Main Render ---
  // Using the improved layout/styling from the previous refactor example
  return (
    <div className="flex flex-col pb-2 gap-2"> {/* Added gap */}

      {/* Button Row for Desktop - Controls Card and Shows Filter Status */}
      <div className="flex items-center gap-2 justify-start"> {/* Changed justify */}

        {/* Button to open/close the Card */}
        <Button
            variant="outline"
            onClick={() => setIsCardOpen(!isCardOpen)}
            className="text-xs w-auto px-3" // Adjust width/padding
            aria-haspopup="true" // Accessibility
            aria-expanded={isCardOpen} // Accessibility
        >
          {isCardOpen ? `Close Legend` : "Cluster Legend"}
        </Button>

        {/* Filter Status/Clear Icon */}
        <div className="flex items-center">
          {filteredCluster !== null ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    onClick={handleClearFilterClick} // Use dedicated handler
                    size="icon"
                    className="rounded-full h-8 w-8" // Adjust size
                  >
                    <FilterX className="h-4 w-4" /> {/* Adjust icon size */}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Clear Filter (Cluster {filteredCluster})</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                   {/* Make disabled button non-interactive */}
                  <span tabIndex={-1}>
                     <Button
                        variant="ghost"
                        size="icon"
                        className="rounded-full h-8 w-8 opacity-50 cursor-not-allowed" // Style disabled state
                        disabled
                        aria-disabled="true"
                     >
                        <Filter className="h-4 w-4" /> {/* Adjust icon size */}
                     </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>No Cluster Filter Applied</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      {/* Desktop: Cluster Legend Card - positioned relative to the button row container */}
       <div className="relative"> {/* Container for positioning the card */}
        {isCardOpen && (
            <Card className="absolute top-0 left-0 w-48 shadow-lg z-50 bg-card"> {/* Adjust width, ensure bg */}
            <CardContent className="p-0"> {/* Remove padding */}
                <ScrollArea className="h-[200px] w-full">
                <ClusterLegendContent />
                </ScrollArea>
            </CardContent>
            </Card>
        )}
       </div>


      {/* Mobile: Drawer */}
      {/* Using a separate Trigger for mobile for clarity */}
      <Drawer>
        <DrawerTrigger asChild>
           <Button variant="outline" className="sm:hidden text-xs w-full mt-2" >
             {filteredCluster !== null ? `Filtered: Cluster ${filteredCluster}` : "View Cluster Legend"}
           </Button>
        </DrawerTrigger>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Cluster Legend</DrawerTitle>
            {filteredCluster !== null && (
              <DrawerDescription>Cluster {filteredCluster} is currently filtered.</DrawerDescription>
            )}
          </DrawerHeader>
          <div className="px-4"> {/* Add padding for scroll area */}
            <ScrollArea className="h-[40vh]"> {/* Use vh for mobile height */}
              {/* Provide a function to close the drawer via DrawerClose trigger */}
              <ClusterLegendContent closeContainer={() => {
                   // Find the DrawerClose button and click it programmatically
                   // This assumes the DrawerClose button exists in the footer below
                   const closeButton = document.getElementById('drawer-close-button');
                   closeButton?.click();
               }} />
            </ScrollArea>
          </div>
          <DrawerFooter>
             <div className="flex flex-row gap-4 justify-end"> {/* Align buttons */}
                {/* Clear Filter Button */}
                {filteredCluster !== null && (
                    <Button
                      variant="outline"
                      onClick={handleClearFilterClick} // Use dedicated handler
                      className="text-xs"
                    >
                       <FilterX className="h-4 w-4 mr-1"/> {/* Add icon */}
                       Clear Filter
                    </Button>
                )}
                 {/* Add an ID to the DrawerClose button for programmatic access */}
                 <DrawerClose asChild>
                    <Button variant="outline" className="text-xs" id="drawer-close-button">Close</Button>
                 </DrawerClose>
             </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

    </div>
  );
};

export default memo(ClusterLegend);